import { ApolloError } from '@apollo/client';
import { UUID } from '@cmg/common';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import usePollingWhenVisible from '../../../../../common/hooks/usePollingWhenVisible';
import { useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess } from '../../../../../common/util/check-access/useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess';
import { CertificateType, OrderBookConfiguration } from '../../../../../graphql';
import { useSideBar_OfferingSellingRestrictions_OfferingQuery } from '../../../components/side-bar/graphql';
import { useBuySideCertificatesModel } from '../../compliance/context/hooks/certificates/buy-side/useCertificatesBuySideModel';
import {
  getCertificateType,
  isReg144A,
} from '../../compliance/context/hooks/certificates/Certificates.model';
import {
  Attestation,
  certificateModelDefaults,
  ComplianceWarning,
} from '../../compliance/context/hooks/certificates/useCertificatesModel';
import { OrderBookPersonaType } from '../../hooks/useIdentifyOrderBookPersona';
import { BuySideIndicationQuery, useBuySideIndicationQuery } from './hooks/buy-side/__generated__';
import { SyndicateIndication_PublishedOfferings_OfferingPartsFragment } from './hooks/syndicate/__generated__';

type AccessChecks = {
  canReadOfferingAndIndication: boolean;
  canUpdateIndication: boolean;
};

export type IndicationActivityBuySideContextType = {
  offeringId: UUID;
  loading: boolean;
  error?: ApolloError;
  basicPublishedOffering?: BuySideIndicationQuery['basicPublishedOffering'];
  publishedOffering: SyndicateIndication_PublishedOfferings_OfferingPartsFragment | null;
  complianceWarning: ComplianceWarning;
  attestation: Attestation | null;
  access: AccessChecks;
  hasActiveRelationship: boolean;
  isReg144A: boolean;
  certificateType: CertificateType | null;
  orderBookConfiguration: Pick<
    OrderBookConfiguration,
    | 'isLimitIndicationsOnlyEnabled'
    | 'isIndicationPriceAsPercentageParEnabled'
    | 'isOnlySharesUnitOnIndicationsEnabled'
  >;
};

const IndicationActivityBuySideContext = React.createContext<IndicationActivityBuySideContextType>({
  offeringId: '',
  loading: false,
  complianceWarning: certificateModelDefaults.warning,
  publishedOffering: null,
  access: {
    canReadOfferingAndIndication: false,
    canUpdateIndication: false,
  },
  attestation: null,
  hasActiveRelationship: false,
  isReg144A: false,
  certificateType: null,
  orderBookConfiguration: {
    isLimitIndicationsOnlyEnabled: false,
    isIndicationPriceAsPercentageParEnabled: false,
    isOnlySharesUnitOnIndicationsEnabled: false,
  },
});

export const useIndicationActivityBuySideContext = () =>
  React.useContext(IndicationActivityBuySideContext);

export type IndicationActivityBuySideContextProps = { offeringId: UUID };

export const IndicationActivityBuySideContextProvider: React.FC<
  React.PropsWithChildren<IndicationActivityBuySideContextProps>
> = ({ children, offeringId }) => {
  const {
    canRead: canReadOfferingAndIndication,
    canUpdate: canUpdateIndication,
    loading: isLoadingAccess,
  } = useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess({
    offeringId,
  });

  const {
    loading: isLoadingOffering,
    data,
    error,
    called,
    startPolling,
    stopPolling,
  } = useBuySideIndicationQuery({
    variables: { offeringId },
    skip: !canReadOfferingAndIndication,
    errorPolicy: 'all',
  });

  const hasActiveRelationship = React.useMemo(() => {
    const managers = data?.publishedOfferings?.items?.[0]?.syndicate?.managers;
    return managers?.some(manager => manager.hasActiveRelationship) ?? false;
  }, [data]);

  usePollingWhenVisible({
    pollInterval: 5_000,
    startPolling,
    stopPolling,
    called: called,
  });

  const {
    data: sellingRestrictionsData,
    error: sellingRestrictionsError,
    loading: sellingRestrictionsLoading,
  } = useSideBar_OfferingSellingRestrictions_OfferingQuery({
    variables: { offeringId },
    skip: !offeringId || !error || !hasActiveRelationship,
  });

  const offering = defaultTo(data?.publishedOfferings?.items?.[0], null);
  const instruments = offering?.instruments ?? [];
  const offeringType = offering?.type;
  const isReg144AValue = instruments[0] ? isReg144A(instruments[0]) : false;
  const certificateType = getCertificateType(offeringType, isReg144AValue);

  const {
    loading: isLoadingCertificates,
    warning: complianceWarning,
    attestation,
  } = useBuySideCertificatesModel({
    type: OrderBookPersonaType.BUY_SIDE,
    offeringId,
    certificateType,
    hasActiveRelationship,
  });

  const context = React.useMemo<IndicationActivityBuySideContextType>(
    () => ({
      offeringId,
      loading:
        isLoadingOffering || isLoadingCertificates || isLoadingAccess || sellingRestrictionsLoading,
      error: error || sellingRestrictionsError,
      basicPublishedOffering: data?.basicPublishedOffering,
      publishedOffering: offering,
      complianceWarning,
      attestation,
      hasActiveRelationship,
      isReg144A: isReg144A(sellingRestrictionsData?.publishedOfferings?.items?.[0]?.instruments[0]),
      access: {
        canReadOfferingAndIndication,
        canUpdateIndication,
      },
      certificateType,
      orderBookConfiguration: {
        isLimitIndicationsOnlyEnabled:
          !!offering?.orderBookConfiguration?.isLimitIndicationsOnlyEnabled,
        isIndicationPriceAsPercentageParEnabled:
          !!offering?.orderBookConfiguration?.isIndicationPriceAsPercentageParEnabled,
        isOnlySharesUnitOnIndicationsEnabled:
          !!offering?.orderBookConfiguration?.isOnlySharesUnitOnIndicationsEnabled,
      },
    }),
    [
      offeringId,
      isLoadingOffering,
      isLoadingCertificates,
      isLoadingAccess,
      sellingRestrictionsLoading,
      error,
      sellingRestrictionsError,
      data?.basicPublishedOffering,
      offering,
      complianceWarning,
      attestation,
      hasActiveRelationship,
      sellingRestrictionsData?.publishedOfferings?.items,
      canReadOfferingAndIndication,
      canUpdateIndication,
      certificateType,
    ]
  );

  return (
    <IndicationActivityBuySideContext.Provider value={context}>
      {children}
    </IndicationActivityBuySideContext.Provider>
  );
};
